import { ReassignEngagementRequest } from '@aiwyn/gql-api/src/models/request/reassign-engagement-request.model';
import { EngagementReassignApiService } from '@aiwyn/gql-api/src/services/secure/v1/engagement/reassign-api.service';
import { DATA_SELECTOR_INJECTION_TOKEN, DataSelectorDirective } from '@aiwyn/shared';
import { InputErrorHintComponent } from '@aiwyn/shared/components';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, finalize, map, take, tap } from 'rxjs/operators';

@Component({
  selector: 'app-engagement-reassignment-form',
  templateUrl: './engagement-reassignment-form.component.html',
  styleUrl: './engagement-reassignment-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputErrorHintComponent, DataSelectorDirective],
  providers: [
    { provide: DATA_SELECTOR_INJECTION_TOKEN, useValue: 'engagement-reassignment-form' },
  ],
})
export class EngagementReassignmentFormComponent {
  private readonly _engagementReassignApiService = inject(EngagementReassignApiService);
  private readonly _destroyRef = inject(DestroyRef);
  
  readonly dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);
  readonly engagementId = input.required<number>();

  readonly reassignmentForm: FormGroup = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    subject: new FormControl('', [Validators.required]),
    message: new FormControl(''),
  });
  
  private readonly _saving$ = new BehaviorSubject<boolean>(false);
  readonly saving$ = this._saving$.asObservable().pipe(
    distinctUntilChanged(),
    tap((saving) => {
      if (saving) {
        this.reassignmentForm.disable();
      } else {
        this.reassignmentForm.enable();
      }
    }),
    takeUntilDestroyed(this._destroyRef),
  );

  submit(): Observable<ReassignEngagementRequest> {
    this.reassignmentForm.updateValueAndValidity();
    this.reassignmentForm.markAllAsTouched();

    if (this.reassignmentForm.invalid) {
      return of(null);
    }

    const { firstName, lastName, email, subject, message } = this.reassignmentForm.getRawValue();

    const reassignEngagementPayload: ReassignEngagementRequest = {
      engagementId: this.engagementId(),
      newContactFirstName: firstName,
      newContactLastName: lastName,
      newContactEmail: email,
      subject,
      message,
    };

    this._saving$.next(true);
    return from(this._engagementReassignApiService.reassignEngagement(reassignEngagementPayload)).pipe(
      take(1),
      filter((response) => response.response),
      map(() => reassignEngagementPayload),
      finalize(() => this._saving$.next(false)),
    );
  }
}
